/**
 * Game Composable
 * 管理遊戲列表、分頁載入、進入遊戲、演示模式
 * 原始來源: cuora-module/src/machine/game_v2_1.js
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/game/game.js)
 * - 業務邏輯：保留在 composable 中
 */

import { nextTick, computed, onBeforeUnmount, getCurrentInstance } from 'vue'
import { storeToRefs } from 'pinia'
import { gameApi } from '@/api'
import { useMainStore } from '@/stores/main'
import { useGameStore } from '@/stores/game/game'
import { useUserInfo } from '@/composables/user/useUserInfo'
import { useAuthToken } from '@/composables/auth/useAuthToken'
import { useSite } from '@/composables/core/useSite'
import { useGameRouter } from '@/composables/game/useGameRouter'
import { isTrue } from '@/utils/siteConfigChecker'

const GAME_SEARCH = 'GameSearch'

export const useGame = () => {
  const nuxtApp = useNuxtApp()
  const { $request } = nuxtApp
  const { appSecret, clientId } = useRuntimeConfig().public
  const { getUserInfo } = useUserInfo()
  const { token } = useAuthToken()
  const { selectWalletEnable, checkActivityWalletRule } = useSite()
  const gameRouter = useGameRouter()
  const store = useGameStore()
  const mainStore = useMainStore()

  const withdrawRule = computed(() => getUserInfo.value?.withdraw_rule)
  const uidSite = computed(() => getUserInfo.value?.uid_site)

  // 統一錯誤處理函數
  const handleGameError = (error, context, fallbackStatus = 'idle') => {
    setGameURL({ gameURL: null })
    store.setGameStatus(fallbackStatus)

    return error
  }

  // 狀態轉換工具函數
  const transitionGameStatus = (fromStatus, toStatus) => {
    if (store.gameStatus.value === fromStatus || !fromStatus) {
      store.setGameStatus(toStatus)
      return true
    }
    return false
  }

  // 清理遊戲狀態的工具函數
  const resetGameState = (clearGameInfo = true) => {
    setGameURL({ gameURL: null })
    if (clearGameInfo) {
      store.clearGameInfo()
    }
    store.setGameStatus('idle')
  }

  const {
    base,
    isLastPage,
    isLoadingMore,
    room,
    index,
    content,
    additionEnable,
    addition,
    game,
    walletType,
    lang,
    isMobile,
    gameURL,
    gameToken,
    gameHtml,
    gameType,
    gameWalletType,
    isPlaying,
    gameStatus,
    hasNext,
    hasRoomAndIsNotInGame,
  } = storeToRefs(store)

  // SSR-safe: 使用 nuxtApp context 存儲 timer，避免記憶體洩漏
  if (!nuxtApp._gameContext) {
    nuxtApp._gameContext = {
      updateTimer: null,
      // SSR cleanup 方法
      cleanup: () => {
        const ctx = nuxtApp._gameContext
        if (ctx?.updateTimer) {
          clearInterval(ctx.updateTimer)
          ctx.updateTimer = null
        }
      },
    }
  }
  const ctx = nuxtApp._gameContext

  const isDifferentRoom = (newRoom) => {
    return room.value?.categoryId !== newRoom?.categoryId
  }

  const initAdditionEnable = (gameInformationSummaryEnable) => {
    const shouldEnable = isTrue(gameInformationSummaryEnable)
    store.setAdditionEnable(shouldEnable)
  }

  const setLastPage = () => {
    store.setIsLastPage(index.value >= content.value?.totalPage - 1)
  }

  const setRoom = (roomValue) => {
    if (roomValue && isDifferentRoom(roomValue)) {
      store.setRoom(roomValue)
      resetContent()
      load()
    }
  }

  const recoverSetRoom = (roomValue) => {
    if (roomValue) {
      store.setRoom(roomValue)
      resetContent()
      load()
    }
  }

  const resetContent = () => {
    store.resetContent()
  }

  const increaseIndex = () => {
    store.increaseIndex()
  }

  const appendContent = (data) => {
    if (room.value?.categoryId === GAME_SEARCH) {
      return
    } else if (additionEnable.value) {
      const [contentData, additionData = {}] = data
      store.setContent({
        ...content.value,
        ...contentData,
        data: (content.value?.data || []).concat(contentData?.data || []),
      })
      store.setAddition({
        ...addition.value,
        ...additionData,
        data: (addition.value?.data || []).concat(additionData?.data || []),
      })
    } else {
      const [contentData] = data
      store.setContent({
        ...content.value,
        ...contentData,
        data: (content.value?.data || []).concat(contentData?.data || []),
      })
      // 當 additionEnable 為 false 時，確保 addition 有正確的結構
      // 避免 addition.value 變成 undefined，防止後續讀取時報錯
      if (!addition.value || !addition.value.data) {
        store.setAddition({
          data: [],
          timestamp: null,
          totalPage: 0,
        })
      }
    }
  }

  const swapContent = (data) => {
    const [contentData, additionData] = data
    if (contentData?.data?.length) {
      store.setContent(contentData)
    }
    if (additionData?.data?.length) {
      store.setAddition(additionData)
    }
  }

  const clearGameInfo = () => {
    store.clearGameInfo()
  }

  const setGameInfo = (gameData) => {
    const { providerId, gameId, gameTypeId, mappingId, portrait, gameCode, name, gameName } = gameData.game
    store.setGame({
      providerId,
      gameId,
      gameTypeId,
      mappingId,
      portrait: portrait ? 1 : 0,
      gameCode,
      name,
      gameName,
    })
  }

  const setGameURL = (data) => {
    store.setGameURL(data.gameURL)
    store.setGameToken(data.gameToken)
    store.setGameHtml(data.gameHtml)
    store.setGameType(data.type)
    store.setGameWalletType(data.gameWalletType)
  }

  const setWalletType = (walletTypeValue) => {
    store.setWalletType(walletTypeValue)
  }

  const setLang = (locale) => {
    store.setLang(locale.replace('-', '_'))
  }

  const setIsPlaying = (isPlayingValue) => {
    store.setIsPlaying(isPlayingValue)
  }

  const setIsLoadingMore = (loadingValue) => {
    store.setIsLoadingMore(loadingValue)
  }

  const getPageContent = async () => {
    if (room.value?.categoryId === GAME_SEARCH) {
      return
    } else if (!token.value) {
      // 檢查 categoryId 是否存在
      if (!room.value?.categoryId) {
        console.warn('[useGame/getPageContent] categoryId is required but not provided')
        return []
      }
      const param = { categoryId: room.value.categoryId, timestamp: null, index: index.value, appSecret }
      const data = await $request.send(gameApi.getListCategory(param))
      return [data]
    } else if (additionEnable.value) {
      // 檢查 categoryId 是否存在
      if (!room.value?.categoryId) {
        console.warn('[useGame/getPageContent] categoryId is required but not provided')
        return []
      }
      const param = { categoryId: room.value.categoryId, timestamp: null, index: index.value, appSecret }
      const [categoryRes, additionRes] = await Promise.all([
        $request.send(gameApi.getListCategory(param)),
        $request.send(gameApi.getListAddition(param)),
      ])
      return [categoryRes, additionRes]
    } else {
      // 檢查 categoryId 是否存在
      if (!room.value?.categoryId) {
        console.warn('[useGame/getPageContent] categoryId is required but not provided')
        return []
      }
      const param = { categoryId: room.value.categoryId, timestamp: null, index: index.value, appSecret }
      const data = await $request.send(gameApi.getListCategory(param))
      return [data]
    }
  }

  const updatePageContent = async () => {
    if (room.value.categoryId === GAME_SEARCH) {
      return []
    } else if (!token.value) {
      return []
    } else if (additionEnable.value) {
      // 檢查 categoryId 是否存在
      if (!room.value?.categoryId) {
        console.warn('[useGame/updatePageContent] categoryId is required but not provided')
        return []
      }
      const param = {
        categoryId: room.value.categoryId,
        timestamp: content.value.timestamp,
        end: index.value,
        appSecret,
      }
      const [categoryRes, additionRes] = await Promise.all([
        $request.send(gameApi.getListCategoryByRange(param)),
        $request.send(gameApi.getListAdditionByRange(param)),
      ])
      return [categoryRes, additionRes]
    } else {
      // 檢查 categoryId 是否存在
      if (!room.value?.categoryId) {
        console.warn('[useGame/updatePageContent] categoryId is required but not provided')
        return []
      }
      const param = {
        categoryId: room.value.categoryId,
        timestamp: content.value.timestamp,
        end: index.value,
        appSecret,
      }
      const data = await $request.send(gameApi.getListCategoryByRange(param))
      return [data]
    }
  }

  const load = async () => {
    // 防止重複載入
    if (isLoadingMore.value) {
      return
    }

    setIsLoadingMore(true)

    try {
      await routing()
      setIsLoadingMore(false)
    } catch (error) {
      setIsLoadingMore(false)
      console.error('[useGame/load] ERROR:', error)
      throw error
    }
  }

  const nextPage = async () => {
    // 防止重複觸發
    if (isLoadingMore.value || isLastPage.value) {
      return
    }

    setIsLoadingMore(true)

    try {
      await routing()
    } catch (error) {
      setIsLoadingMore(false)
      console.error('[useGame/nextPage] ERROR:', error)
      throw error
    }
  }

  const routing = async () => {
    if (hasNext.value) {
      await loadPage()
    } else {
      setIsLoadingMore(false)
    }
  }

  const loadPage = async () => {
    store.setGameStatus('loading')
    increaseIndex()

    try {
      const data = await getPageContent()
      appendContent(data)

      setLastPage()

      store.setGameStatus('loaded')

      await nextTick()

      setIsLoadingMore(false)

      store.setGameStatus('idle')
    } catch (err) {
      setIsLoadingMore(false)
      store.setGameStatus('idle')
      console.error('[useGame/loadPage] ERROR:', err)
    }
  }

  const update = async () => {
    store.setGameStatus('updating')

    try {
      const data = await updatePageContent()
      swapContent(data)
    } catch (err) {
      console.error('[useGame] update error:', err)
    } finally {
      store.setGameStatus('idle')
    }
  }

  const connectGame = async () => {
    const returnProviders = ['RSG', 'KP', 'EPICWIN', 'AMBSLOT', 'SEXY_VENUS', 'SEXY_SEXYBCRT', 'PG', 'NPG']
    const { providerId, gameId, gameTypeId, mappingId } = game.value

    const params = {
      provider_id: providerId,
      game_id: gameId,
      gametype_id: gameTypeId,
      mapping_id: mappingId,
      is_demo: false,
      is_mobile: isMobile.value === 'mobile' ? 'y' : null,
      lang: lang.value,
      selected_wallet_type: walletType.value,
      return_url: returnProviders.includes(providerId) ? `${window.location.origin}${base.value}return` : window.location.href?.split('?')?.[0],
    }

    const data = await $request.send(gameApi.playGame(params))
    const { type, redirect_url, gameToken: token, gameHtml: html, wallet_type } = data
    return { type, gameToken: token, gameHtml: html, gameURL: redirect_url, gameWalletType: wallet_type }
  }

  const demoGameConnect = async () => {
    const { providerId, gameId, gameTypeId, mappingId } = game.value

    const params = {
      site: clientId,
      provider_id: providerId,
      game_id: gameId,
      gametype_id: gameTypeId,
      mapping_id: mappingId,
      is_mobile: isMobile.value === 'mobile' ? 'y' : null,
      lang: lang.value,
      appSecret,
    }

    const data = await $request.send(gameApi.demoGame(params))
    const { type, redirect_url, gameToken: token, gameHtml: html, is_demo } = data
    const updatedGame = { ...game.value, isDemo: is_demo }
    store.setGame(updatedGame)
    return { type, gameToken: token, gameHtml: html, gameURL: redirect_url, game: updatedGame }
  }

  const disconnectGame = async (uidSite) => {
    const providerId = game.value.providerId
    // [防呆] 若缺失 providerId 則不進行離開遊戲請求
    if(!providerId){
      return
    }
    const data = await $request.send(
      gameApi.logoutGame({
        uid_site: uidSite,
        provider_id: providerId,
      })
    )
    return data
  }

  const playGame = async (gameData) => {
    const checkWalletRule = checkActivityWalletRule.value // 是否需要判斷當前活動規則
    if (!selectWalletEnable.value) {
      // 不需選擇錢包，直接進入遊戲
      const onSuccessCallback = gameRouter.createGameSuccessCallback()
      enterGame(gameData, onSuccessCallback)
      return
    }

    mainStore.setModal('ModalSelectWallet')
    if (checkWalletRule) {
      if (withdrawRule.value === 'DEFAULT') {
        // 判斷規則_現金錢包
        mainStore.setModalParams({ doubleWallet: false, gameData })
      } else {
        // 判斷規則_活動與現金錢包
        mainStore.setModalParams({ doubleWallet: true, gameData })
      }
    } else {
      // 不需判斷規則_顯示活動與現金錢包
      mainStore.setModalParams({ doubleWallet: true, gameData })
    }
  }

  const enterGame = async (gameData, onSuccessCallback = null) => {
    try {
      clearGameInfo()
      transitionGameStatus(null, 'connecting')
      setGameInfo(gameData)
      setLang(gameData.locale)
      setWalletType(gameData.walletType)

      const result = await connectGame()
      setGameURL(result)
      transitionGameStatus('connecting', 'playing')

      // 執行成功回調（如路由跳轉）
      if (onSuccessCallback && typeof onSuccessCallback === 'function') {
        await onSuccessCallback(gameData)
      }
    } catch (err) {
      handleGameError(err, 'enterGame')
      throw err // 重新拋出錯誤供上層處理
    }
  }

  const enterDemoGame = async (gameData, onSuccessCallback = null) => {
    try {
      clearGameInfo()
      transitionGameStatus(null, 'demoGame')
      setGameInfo(gameData)
      setLang(gameData.locale)

      const result = await demoGameConnect()
      setGameURL(result)
      transitionGameStatus('demoGame', 'playingDemo')

      // 執行成功回調
      if (onSuccessCallback && typeof onSuccessCallback === 'function') {
        await onSuccessCallback(gameData)
      }
    } catch (err) {
      handleGameError(err, 'demoGame')
      throw err
    }
  }

  const exitGame = async () => {
    try {
      transitionGameStatus(null, 'disconnect')

      // [偵錯] 參數錯誤記錄 (game/logout)
      const providerId = game.value?.providerId
      if (nuxtApp.$sentry && !providerId) {
        nuxtApp.$sentry.captureMessage('PARAMETER_ERROR', {
          level: 'error',
          tags: {
            caller: 'exitGame',
            at: 'game/useGame.js',
            providerId: providerId ,
          }
        })
      }

      await disconnectGame(uidSite.value)
      setGameURL({ gameURL: null })
    } catch (err) {
      handleGameError(err, 'exitGame')
      throw err
    } finally {
      resetGameState(true)
    }
  }

  const exitDemoGame = () => {
    clearGameInfo()
    store.setGameStatus('idle')
  }

  const refreshGame = async (onSuccessCallback = null) => {
    store.setGameStatus('refreshing')

    try {
      await disconnectGame(uidSite.value)
      setGameURL({ gameURL: null })

      store.setGameStatus('refreshPending')
      await new Promise((resolve) => setTimeout(resolve, 500))

      const result = await connectGame()
      setGameURL(result)
      store.setGameStatus('connecting')

      // 執行成功回調
      if (onSuccessCallback && typeof onSuccessCallback === 'function') {
        await onSuccessCallback(result)
      }
    } catch (err) {
      setGameURL({ gameURL: null })
      store.setGameStatus('idle')
      console.error('[useGame] refreshGame error:', err)
      throw err
    }
  }

  const startAutoUpdate = async () => {
    if (!import.meta.client) return

    stopAutoUpdate()

    // 立即執行一次更新
    if (hasRoomAndIsNotInGame.value) {
      await update()
    }

    // 開始定時更新
    ctx.updateTimer = setInterval(async () => {
      if (hasRoomAndIsNotInGame.value) {
        await update()
      }
    }, 20000)
  }

  const stopAutoUpdate = () => {
    if (!import.meta.client) return

    if (ctx.updateTimer) {
      clearInterval(ctx.updateTimer)
      ctx.updateTimer = null
    }
  }

  // 元件卸載時清理 timer，避免記憶體洩漏
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      stopAutoUpdate()
    })
  }

  return {
    base,
    isLastPage,
    isLoadingMore,
    room,
    index,
    content,
    additionEnable,
    addition,
    game,
    walletType,
    lang,
    isMobile,
    gameURL,
    gameToken,
    gameHtml,
    gameType,
    gameWalletType,
    isPlaying,
    gameStatus,
    hasNext,
    hasRoomAndIsNotInGame,
    // 工具函數
    initAdditionEnable,
    setRoom,
    recoverSetRoom,
    load,
    nextPage,
    // 遊戲控制函數（已優化）
    playGame,
    enterGame,
    enterDemoGame,
    connectGame,
    exitGame,
    exitDemoGame,
    refreshGame,
    // 狀態管理函數
    resetContent,
    resetGameState,
    transitionGameStatus,
    handleGameError,
    setIsPlaying,
    setIsLoadingMore,
    setWalletType,
    setLang,
    // 自動更新
    startAutoUpdate,
    stopAutoUpdate,
  }
}
